import { Combine } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import { useParent } from './ParentContext';

/** What merging one family into another would do (#509), from the server's merge preview. */
export interface MergePreview {
  into: { id: string; name: string };
  from: { id: string; name: string };
  settings: { key: string; label: string; into: string; from: string; same: boolean; canKeepBoth: boolean }[];
  children: { id: string; name: string }[];
  adults: { id: string; name: string; email: string }[];
}
export type MergeChoice = 'into' | 'from' | 'both';
export type MergeChoices = Record<string, MergeChoice>;
export interface MergeRequests {
  incoming: { id: string; requestedBy: string; expiresAt: number; preview: MergePreview }[];
  outgoing: { id: string; intoId: string; fromName: string; expiresAt: number }[];
}

/**
 * The side-by-side for merging two families (#509): each family setting with both families' values, and which one the
 * combined family keeps (holidays can keep both). Settings that are the same have nothing to choose. Used by the
 * family owner answering a request and by an admin merging for a customer.
 */
export function MergeChoicesTable({ preview, choices, onChange }: { preview: MergePreview; choices: MergeChoices; onChange: (c: MergeChoices) => void }) {
  return (
    <div className="stack merge-choices">
      <table className="permissions-grid">
        <thead>
          <tr>
            <th>Keep</th>
            <th>{preview.into.name}</th>
            <th>{preview.from.name}</th>
          </tr>
        </thead>
        <tbody>
          {preview.settings.map((s) => {
            const pick = choices[s.key] ?? 'into';
            const option = (value: MergeChoice, text: string) => (
              <label className="check merge-option">
                <input type="radio" name={`merge-${preview.from.id}-${s.key}`} checked={pick === value} onChange={() => onChange({ ...choices, [s.key]: value })} />
                <span>{text}</span>
              </label>
            );
            return (
              <tr key={s.key}>
                <td>
                  {s.label}
                  {s.canKeepBoth && !s.same && <div className="small">{option('both', 'Keep both')}</div>}
                </td>
                {s.same ? (
                  <td colSpan={2} className="muted small">
                    {s.into} <span className="muted">(the same)</span>
                  </td>
                ) : (
                  <>
                    <td>{option('into', s.into)}</td>
                    <td>{option('from', s.from)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">
        Moving to {preview.into.name}: {preview.children.length ? preview.children.map((c) => c.name).join(', ') : 'no students'} (with their devices, history,
        grades and planner){preview.adults.length ? `, and ${preview.adults.map((a) => a.name).join(', ')}` : ''}. To-dos are combined. {preview.into.name} keeps its plan, and{' '}
        {preview.from.name} is removed.
      </p>
    </div>
  );
}

/** A request to merge your family into someone else's (#509): review it, choose each setting, and accept or decline. */
export function IncomingMerge({ request, onDone }: { request: MergeRequests['incoming'][number]; onDone: () => void }) {
  const { toast, refresh } = useParent();
  const [choices, setChoices] = useState<MergeChoices>({});
  const [busy, setBusy] = useState(false);
  const p = request.preview;
  async function accept() {
    if (!confirm(`Combine ${p.from.name} into ${p.into.name}? This can’t be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/api/family/merge-requests/${request.id}/accept`, { choices });
      toast({ text: `${p.from.name} is now part of ${p.into.name}.`, tone: 'good' });
      await refresh();
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function decline() {
    await api.del(`/api/family/merge-requests/${request.id}`).catch(() => undefined);
    onDone();
  }
  return (
    <div className="family-card merge-request">
      <div className="family-card-head">
        <Combine size={16} />
        <strong>
          {request.requestedBy} wants to combine {p.from.name} into {p.into.name}
        </strong>
      </div>
      <p className="muted small">One family, one plan. Choose what the combined family keeps. Expires {relative(request.expiresAt)}.</p>
      <MergeChoicesTable preview={p} choices={choices} onChange={setChoices} />
      <div className="row-gap">
        <button className="btn primary" onClick={accept} disabled={busy}>
          {busy ? 'Combining…' : 'Combine families'}
        </button>
        <button className="btn" onClick={decline} disabled={busy}>
          Decline
        </button>
      </div>
    </div>
  );
}

/** The owner asks another family's owner to merge their family into this one (#509). */
export function RequestMerge({ familyId, outgoing, onChange }: { familyId: string; outgoing: MergeRequests['outgoing']; onChange: () => void }) {
  const { toast } = useParent();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = outgoing.filter((o) => o.intoId === familyId);
  async function send() {
    setBusy(true);
    try {
      const r = await api.post<{ emailed: boolean }>(`/api/family/${familyId}/merge-requests`, { email: email.trim() });
      toast({ text: r.emailed ? 'Request sent. They’ll get an email to review it.' : 'Request sent. They’ll see it in Settings.', tone: 'good' });
      setEmail('');
      setOpen(false);
      onChange();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack merge-request-out">
      {mine.map((o) => (
        <div key={o.id} className="row-between">
          <span className="muted small">
            Waiting for {o.fromName} to combine into this family (expires {relative(o.expiresAt)})
          </span>
          <button className="btn small" onClick={() => api.del(`/api/family/merge-requests/${o.id}`).then(onChange)}>
            Cancel
          </button>
        </div>
      ))}
      {open && <p className="muted small">Combine another family into this one: enter its owner’s email. They choose which settings to keep.</p>}
      {open ? (
        <div className="row-between family-invite-row">
          <input className="grow" type="email" placeholder="owner@example.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Other family's owner" />
          <button className="btn small primary" onClick={send} disabled={busy || !email.includes('@')}>
            Send request
          </button>
          <button className="btn small" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button className="btn small merge-start" onClick={() => setOpen(true)}>
          <Combine size={14} /> Combine with another family
        </button>
      )}
    </div>
  );
}
